import { useCallback, useEffect, useState } from "react";
import { UploadedFile } from "../types/UploadedFile.type";

interface UseFileUploadStateParams {
	files: UploadedFile[];
	multiple: boolean;
	onChange: (files: UploadedFile[] | null) => void;
}

export function useFileUploadState({
	multiple,
	onChange,
	files: initialFiles,
}: UseFileUploadStateParams) {
	// ensure files are never null
	const [files, setFiles] = useState<UploadedFile[]>(() => initialFiles ?? []);

	// Sync state when initialFiles changes (e.g., async data load)
	useEffect(() => {
		if (initialFiles && initialFiles.length > 0) {
			setFiles(initialFiles);
		}
	}, [initialFiles]);

	const addFiles = useCallback(
		(newFiles: UploadedFile[]) => {
			setFiles((prev) => {
				const newState = multiple
					? [...prev, ...newFiles]
					: newFiles.slice(0, 1);
				onChange?.(newState);
				return newState;
			});
		},
		[multiple, onChange]
	);

	const updateUploadedFile = useCallback(
		(uploadId: string, updatedFile: UploadedFile) => {
			setFiles((prev) => {
				const newState = prev.map((file) => {
					if (file.uploadId === uploadId) {
						return { ...updatedFile };
					}
					return file;
				});
				onChange?.(newState);
				return newState;
			});
		},
		[onChange]
	);

	const removeFile = useCallback(
		(id: number | string) => {
			setFiles((prev) => {
				const newState = prev.filter((f) => {
					if (typeof id === "string") {
						return f.uploadId !== id;
					}
					return f.id !== id;
				});
				onChange?.(newState);
				return newState;
			});
		},
		[onChange]
	);

	const clearFiles = useCallback(() => {
		setFiles([]);
		onChange?.([]);
	}, [onChange]);

	const replaceFiles = useCallback((newFiles: UploadedFile[]) => {
		setFiles(newFiles);
		onChange?.(newFiles);
	}, [onChange]);

	return {
		files: files,
		addFiles,
		clearFiles,
		removeFile,
		replaceFiles,
		updateUploadedFile,
	};
}
